import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, PieChart, Pie, Cell, ScatterChart, Scatter,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  TrendingUp, Bot, Target, Wrench, LayoutDashboard, Rocket,
  ChevronLeft, ChevronRight, X, Info,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ---------- synthetic dataset (internally consistent, pre-audited) ----------
const PROPERTIES = [
  { id: 'villa-claude', name: 'Villa Claude',          region: 'Guanacaste, CR', invested: 1850000, revenue: 320000,  opex: 118000 },
  { id: 'casamar',      name: 'Casamar',               region: 'Los Sueños, CR', invested: 1620000, revenue: 240000,  opex: 104000 },
  { id: 'palm-jumeirah',name: 'Villa Palm Jumeirah',   region: 'Palm Jumeirah, UAE', invested: 15000000, revenue: 1480000, opex: 610000 },
];
const calc = (p) => {
  const noi = p.revenue - p.opex;
  const margin = (noi / p.revenue) * 100;
  const roi = (noi / p.invested) * 100;
  return { ...p, noi, margin, roi, cashFlow: noi };
};
const DATA = PROPERTIES.map(calc);
const PORTFOLIO = (() => {
  const invested = DATA.reduce((s, p) => s + p.invested, 0);
  const revenue  = DATA.reduce((s, p) => s + p.revenue,  0);
  const opex     = DATA.reduce((s, p) => s + p.opex,     0);
  const noi      = revenue - opex;
  return { invested, revenue, opex, noi, margin: (noi / revenue) * 100, roi: (noi / invested) * 100, cashFlow: noi };
})();
// 12-month synthetic series (seasonally weighted but with stable yearly totals)
const SEASON = [0.06, 0.06, 0.09, 0.11, 0.10, 0.09, 0.07, 0.06, 0.07, 0.09, 0.09, 0.11]; // sums to 1.00
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHLY = MONTHS.map((m, i) => ({
  month: m,
  revenue: Math.round(PORTFOLIO.revenue * SEASON[i]),
  opex:    Math.round(PORTFOLIO.opex    * (0.075 + (i % 3) * 0.004)),
}));
// corrected scenario (CEO-approved: uses Villa Claude's margin rounded to 1 dp = 63.1%)
const CASAMAR_SCENARIO = (() => {
  const target = DATA.find((p) => p.id === 'casamar');
  const leader = DATA.find((p) => p.id === 'villa-claude');
  const leaderMarginRounded = Math.round(leader.margin * 10) / 10;           // 63.1
  const scenarioNoi = Math.round(target.revenue * (leaderMarginRounded / 100)); // 240000 * 0.631 = 151,440
  const scenarioRoi = (scenarioNoi / target.invested) * 100;                 // ≈ 9.35%
  return {
    currentNoi: target.noi,                              // 136,000
    scenarioNoi,                                         // 151,440
    noiDelta: scenarioNoi - target.noi,                  // 15,440
    pctDelta: ((scenarioNoi - target.noi) / target.noi) * 100, // 11.35%
    currentRoi: target.roi,                              // 8.40
    scenarioRoi,                                         // 9.35
    leaderMarginRounded,                                 // 63.1
  };
})();

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const moneyK = (n) => `$${(n / 1000).toFixed(0)}K`;
const pct  = (n, d = 1) => `${n.toFixed(d)}%`;

// ---------- animated number ----------
const CountUp = ({ to, format = (v) => v.toFixed(0), duration = 600 }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let start = null;
    let raf = 0;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setV(to * (0.1 + 0.9 * p));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setV(to);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{format(v)}</>;
};

// ---------- small building blocks ----------
const Pill = ({ children, tone = 'gold' }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
      tone === 'gold' ? 'bg-gold-metallic/15 text-gold-text border border-gold-metallic/30' :
      tone === 'navy' ? 'bg-navy-deep/10 text-navy-deep border border-navy-deep/20' :
      'bg-emerald-50 text-emerald-700 border border-emerald-200'
    }`}
  >
    {children}
  </span>
);
const KpiTile = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-border rounded-sm p-4 flex flex-col" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-playfair text-2xl md:text-3xl text-navy-deep mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);
const Disclosure = ({ children, testid }) => (
  <p
    className="text-[11px] leading-snug text-muted-foreground flex items-start gap-1.5 mt-3"
    data-testid={testid}
  >
    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
    <span>{children}</span>
  </p>
);

// ---------- step wrappers ----------
const StepFrame = ({ icon: Icon, eyebrow, title, outcome, children, testid }) => (
  <div className="space-y-5" data-testid={testid}>
    <header className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-navy-deep text-gold-metallic flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold-text">{eyebrow}</span>
      </div>
      <h3 className="font-playfair text-xl md:text-2xl text-navy-deep">{title}</h3>
      <p className="text-sm md:text-base text-foreground leading-relaxed" data-testid={`${testid}-outcome`}>
        {outcome}
      </p>
    </header>
    {children}
  </div>
);

// =============================================================================
//                                 MAIN COMPONENT
// =============================================================================
const RoiGuidedDemoModal = ({ open, onOpenChange, onRequestDemo, onGetStarted }) => {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const tr = (es, en) => (isEs ? es : en);

  const [stepIdx, setStepIdx] = useState(0);
  const [audience, setAudience] = useState('owners');
  const TOTAL = 6;

  // reset when opened
  useEffect(() => { if (open) { setStepIdx(0); } }, [open]);

  // keyboard nav
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') setStepIdx((i) => Math.min(TOTAL - 1, i + 1));
      else if (e.key === 'ArrowLeft') setStepIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const steps = useMemo(() => [
    {
      key: 'command-center',
      icon: LayoutDashboard,
      eyebrow: tr('PASO 1 · CENTRO DE MANDO FINANCIERO', 'STEP 1 · FINANCIAL COMMAND CENTER'),
      title: tr('Lo que tus villas ganan y lo que realmente te queda',
                'What your villas earn — and what you actually keep'),
      outcome: audience === 'owners'
        ? tr(
            `Tu portafolio generó ${moneyK(PORTFOLIO.noi)} de ingreso operativo neto con un margen del ${pct(PORTFOLIO.margin)}.`,
            `Your portfolio generated ${moneyK(PORTFOLIO.noi)} of net operating income at a ${pct(PORTFOLIO.margin)} margin.`
          )
        : tr(
            `El portafolio bajo tu gestión produjo ${moneyK(PORTFOLIO.noi)} de NOI (${pct(PORTFOLIO.margin)} margen).`,
            `The portfolio under your management produced ${moneyK(PORTFOLIO.noi)} of NOI (${pct(PORTFOLIO.margin)} margin).`
          ),
    },
    {
      key: 'ai-cfo',
      icon: Bot,
      eyebrow: tr('PASO 2 · AI CFO · INTELIGENCIA FINANCIERA', 'STEP 2 · AI CFO · FINANCIAL INTELLIGENCE'),
      title: tr('Dónde tus números te están pidiendo atención',
                'Where your numbers are asking for attention'),
      outcome: tr(
        'El AI CFO señala 3 líneas de gasto por encima de la mediana del portafolio. Oportunidades a revisar, no ahorros comprometidos.',
        'The AI CFO flags 3 expense lines above the portfolio median. Opportunities to review — not committed savings.'
      ),
    },
    {
      key: 'roi',
      icon: Target,
      eyebrow: tr('PASO 3 · ROI · RENDIMIENTO DE INVERSIÓN', 'STEP 3 · ROI · INVESTMENT PERFORMANCE'),
      title: tr('Cuánto trabaja tu capital, villa por villa',
                'How hard your capital is working, villa by villa'),
      outcome: tr(
        `ROI estimado anual del portafolio: ${pct(PORTFOLIO.roi)}. Mayor rendimiento: Villa Claude al ${pct(DATA[0].roi)}.`,
        `Portfolio estimated annual ROI: ${pct(PORTFOLIO.roi)}. Highest performer: Villa Claude at ${pct(DATA[0].roi)}.`
      ),
    },
    {
      key: 'operations',
      icon: Wrench,
      eyebrow: tr('PASO 4 · EFICIENCIA OPERATIVA', 'STEP 4 · OPERATIONAL EFFICIENCY'),
      title: tr('Transparencia sobre lo que pagas cada mes',
                'Clarity on what you pay every month'),
      outcome: audience === 'owners'
        ? tr(
            'El mayor bloque de tu OpEx es recurrente. Negociarlo con datos es más fácil que descubrirlo tarde.',
            'The largest share of your OpEx is recurring. Negotiating it with data is easier than discovering it late.'
          )
        : tr(
            'Tickets, categorías de gasto y estado de proveedores centralizados por propiedad.',
            'Tickets, expense categories and vendor status centralised by property.'
          ),
    },
    {
      key: 'portfolio',
      icon: TrendingUp,
      eyebrow: tr('PASO 5 · INTELIGENCIA DE PORTAFOLIO', 'STEP 5 · PORTFOLIO INTELLIGENCE'),
      title: tr('Dónde invertir el próximo dólar — y dónde no',
                'Where to put the next dollar — and where not to'),
      outcome: tr(
        `Brecha de ROI: Villa Claude → Casamar = ${pct(DATA[0].roi - DATA[1].roi)} pp · Villa Claude → Palm Jumeirah = ${pct(DATA[0].roi - DATA[2].roi)} pp.`,
        `ROI gap: Villa Claude → Casamar = ${pct(DATA[0].roi - DATA[1].roi)} pp · Villa Claude → Palm Jumeirah = ${pct(DATA[0].roi - DATA[2].roi)} pp.`
      ),
    },
    {
      key: 'convert',
      icon: Rocket,
      eyebrow: tr('PASO 6 · ES TU TURNO', 'STEP 6 · YOUR TURN'),
      title: tr('Descubre el ROI de tus propiedades',
                `Discover your property's ROI`),
      outcome: tr(
        'Agenda una demo personal con datos reales de tu portafolio, o prueba la plataforma 14 días.',
        'Book a personal demo on your real portfolio, or try the platform free for 14 days.'
      ),
    },
  ], [audience, isEs]); // eslint-disable-line

  const step = steps[stepIdx];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl w-full bg-sand-warm p-0 overflow-hidden border-0"
        data-testid="roi-tour-modal"
      >
        {/* Chrome */}
        <div className="flex items-center justify-between px-5 md:px-7 py-4 bg-navy-deep text-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-sm bg-white/10 p-0.5" data-testid="roi-tour-audience-switch">
              <button
                type="button"
                onClick={() => setAudience('owners')}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors ${audience === 'owners' ? 'bg-gold-metallic text-navy-deep' : 'text-white/80 hover:text-white'}`}
                data-testid="roi-tour-audience-owners"
              >
                {tr('Propietario', 'Villa Owner')}
              </button>
              <button
                type="button"
                onClick={() => setAudience('managers')}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors ${audience === 'managers' ? 'bg-gold-metallic text-navy-deep' : 'text-white/80 hover:text-white'}`}
                data-testid="roi-tour-audience-managers"
              >
                {tr('Gestor', 'Property Manager')}
              </button>
            </div>
            <Pill tone="gold">{tr('Datos ilustrativos de demo', 'Illustrative Demo Data')}</Pill>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-white/70" data-testid="roi-tour-step-indicator">
              {stepIdx + 1} / {TOTAL}
            </span>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1 text-white/80 hover:text-white rounded-sm"
              data-testid="roi-tour-close"
              aria-label={tr('Cerrar recorrido', 'Close tour')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 md:px-7 pt-3">
          <Progress value={((stepIdx + 1) / TOTAL) * 100} className="h-1" />
        </div>

        {/* Step body */}
        <div className="px-5 md:px-7 py-6 max-h-[70vh] overflow-y-auto">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step.key}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
            >
              {stepIdx === 0 && (
                <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-1">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiTile label={tr('Ingresos', 'Revenue')}       value={<CountUp to={PORTFOLIO.revenue} format={moneyK} />} testid="kpi-revenue" />
                    <KpiTile label={tr('Gastos operativos', 'OpEx')}  value={<CountUp to={PORTFOLIO.opex}    format={moneyK} />} testid="kpi-opex" />
                    <KpiTile label={tr('NOI', 'NOI')}                 value={<CountUp to={PORTFOLIO.noi}     format={moneyK} />}
                             sub={tr('Ingresos − Gastos', 'Revenue − OpEx')} testid="kpi-noi" />
                    <KpiTile label={tr('Margen de utilidad', 'Profit margin')} value={<CountUp to={PORTFOLIO.margin} format={(v) => pct(v)} />}
                             sub={tr('NOI / Ingresos', 'NOI / Revenue')} testid="kpi-margin" />
                  </div>
                  <div className="bg-white border border-border rounded-sm p-3 h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={MONTHLY} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1A2B4A" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#1A2B4A" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="op" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#B8860B" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#B8860B" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="#eee" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                        <RTooltip formatter={(v) => `$${v.toLocaleString()}`} />
                        <Area type="monotone" dataKey="revenue" stroke="#1A2B4A" strokeWidth={2} fill="url(#rev)" />
                        <Area type="monotone" dataKey="opex"    stroke="#B8860B" strokeWidth={2} fill="url(#op)"  />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <Disclosure testid="roi-disclosure-s1">
                    {tr(
                      'Serie mensual sintética para la demo. El total anual coincide con el dataset ilustrativo.',
                      'Synthetic monthly series for the demo. Annual totals match the illustrative dataset.'
                    )}
                  </Disclosure>
                </StepFrame>
              )}

              {stepIdx === 1 && (
                <AiCfoStep
                  step={step}
                  isEs={isEs}
                  tr={tr}
                />
              )}

              {stepIdx === 2 && (
                <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {DATA.map((p) => (
                      <RoiDial key={p.id} label={p.name} value={p.roi} />
                    ))}
                    <RoiDial label={tr('Portafolio', 'Portfolio')} value={PORTFOLIO.roi} highlight />
                  </div>
                  <div className="bg-white border border-border rounded-sm p-4 text-sm leading-relaxed" data-testid="roi-formula">
                    <div className="flex items-center gap-2 mb-2">
                      <Pill tone="navy">{tr('FÓRMULA', 'FORMULA')}</Pill>
                    </div>
                    <p className="text-navy-deep">
                      <strong>{tr('ROI anual estimado', 'Est. annual ROI')}</strong> ={' '}
                      ({tr('NOI anual', 'Annual NOI')}) ÷ ({tr('Capital invertido', 'Invested capital')}) × 100
                    </p>
                    <p className="text-muted-foreground mt-1">
                      <strong>{tr('Retorno sobre efectivo', 'Cash-on-cash return')}</strong> ={' '}
                      ({tr('Flujo de caja operativo', 'Operating cash flow')}) ÷ ({tr('Efectivo invertido', 'Cash invested')}) × 100
                    </p>
                    <p className="text-xs text-muted-foreground mt-3">
                      {tr(
                        'En este dataset el servicio de deuda es 0, por lo que el ROI sin apalancamiento y el retorno sobre efectivo coinciden.',
                        'In this dataset debt service is 0, so unleveraged ROI and cash-on-cash return are equal.'
                      )}
                    </p>
                  </div>
                  <Disclosure testid="roi-disclosure-s3">
                    {tr(
                      'ROI sin apalancamiento, ocupación anual completa, impuestos excluidos. Datos históricos sintéticos. No es un pronóstico.',
                      'Unleveraged ROI, full-year occupancy assumed, taxes excluded. Synthetic historical data. Not a forecast.'
                    )}
                  </Disclosure>
                </StepFrame>
              )}

              {stepIdx === 3 && (
                <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-border rounded-sm p-3 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={EXPENSE_MIX}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                          >
                            {EXPENSE_MIX.map((e, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <RTooltip formatter={(v, n) => [`${v}%`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-white border border-border rounded-sm p-4 text-sm space-y-2">
                      <div className="flex items-center justify-between font-semibold text-navy-deep">
                        <span>{tr('Categoría', 'Category')}</span>
                        <span>{tr('% del OpEx', '% of OpEx')}</span>
                      </div>
                      {EXPENSE_MIX.map((e, i) => (
                        <div key={e.key} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                            {isEs ? e.es : e.name}
                          </span>
                          <span className="font-mono tabular-nums">{e.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Disclosure testid="roi-disclosure-s4">
                    {tr(
                      'Distribución ilustrativa. Centraliza aprobaciones y vendors por propiedad; no prometemos ahorros específicos.',
                      'Illustrative split. Centralises approvals and vendors per property; no specific savings are promised.'
                    )}
                  </Disclosure>
                </StepFrame>
              )}

              {stepIdx === 4 && (
                <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-5">
                  <div className="bg-white border border-border rounded-sm overflow-hidden">
                    <table className="w-full text-xs md:text-sm" data-testid="portfolio-comparison-table">
                      <thead className="bg-sand-warm">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2">{tr('Propiedad', 'Property')}</th>
                          <th className="px-3 py-2 text-right">{tr('Ingresos', 'Revenue')}</th>
                          <th className="px-3 py-2 text-right">{tr('Margen', 'Margin')}</th>
                          <th className="px-3 py-2 text-right">ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {DATA.map((p) => {
                          const leader = p.roi === Math.max(...DATA.map((x) => x.roi));
                          return (
                            <tr key={p.id} className="border-t border-border">
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  {leader && <Pill tone="gold">{tr('Líder', 'Leader')}</Pill>}
                                  <span className="font-medium text-navy-deep">{p.name}</span>
                                </div>
                                <div className="text-[10px] text-muted-foreground">{p.region}</div>
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums">{moneyK(p.revenue)}</td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums">{pct(p.margin)}</td>
                              <td className={`px-3 py-2 text-right font-mono tabular-nums ${leader ? 'text-emerald-rich font-bold' : 'text-navy-deep'}`}>{pct(p.roi)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-navy-deep text-white rounded-sm p-4" data-testid="casamar-scenario">
                    <div className="flex items-center gap-2 mb-2">
                      <Pill tone="gold">{tr('ESCENARIO ILUSTRATIVO', 'ILLUSTRATIVE SCENARIO')}</Pill>
                    </div>
                    <p className="text-sm leading-relaxed">
                      {tr(
                        <>Si Casamar alcanzara el margen de Villa Claude ({pct(CASAMAR_SCENARIO.leaderMarginRounded)}) <strong>con los ingresos actuales</strong>, su NOI pasaría de <strong>{money(CASAMAR_SCENARIO.currentNoi)}</strong> a <strong>{money(CASAMAR_SCENARIO.scenarioNoi)}</strong> (+{money(CASAMAR_SCENARIO.noiDelta)} / +{pct(CASAMAR_SCENARIO.pctDelta)}) y su ROI subiría de <strong>{pct(CASAMAR_SCENARIO.currentRoi)}</strong> a aprox. <strong>{pct(CASAMAR_SCENARIO.scenarioRoi)}</strong>.</>,
                        <>If Casamar reached Villa Claude's margin ({pct(CASAMAR_SCENARIO.leaderMarginRounded)}) <strong>at current revenue</strong>, NOI would move from <strong>{money(CASAMAR_SCENARIO.currentNoi)}</strong> to <strong>{money(CASAMAR_SCENARIO.scenarioNoi)}</strong> (+{money(CASAMAR_SCENARIO.noiDelta)} / +{pct(CASAMAR_SCENARIO.pctDelta)}) and ROI would rise from <strong>{pct(CASAMAR_SCENARIO.currentRoi)}</strong> to ~<strong>{pct(CASAMAR_SCENARIO.scenarioRoi)}</strong>.</>
                      )}
                    </p>
                    <p className="text-[11px] text-white/70 mt-2">
                      {tr(
                        'Aritmética ilustrativa a ingresos constantes. No es un pronóstico ni una garantía.',
                        'Illustrative arithmetic at constant revenue. Not a forecast, not a guarantee.'
                      )}
                    </p>
                  </div>
                </StepFrame>
              )}

              {stepIdx === 5 && (
                <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-6">
                  <div className="bg-white border border-border rounded-sm p-6 md:p-8 flex flex-col items-start gap-4">
                    <h4 className="font-playfair text-xl md:text-2xl text-navy-deep">
                      {tr('Hablemos de tu portafolio real.', `Let's talk about your real portfolio.`)}
                    </h4>
                    <p className="text-sm text-muted-foreground max-w-xl">
                      {tr(
                        'Agenda una demo personalizada y te mostramos cómo se vería este mismo recorrido con tus propiedades y tus números.',
                        'Book a personal demo and we will show you this same tour using your properties and your numbers.'
                      )}
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <Button
                        className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep rounded-sm px-6 py-5 font-semibold"
                        onClick={() => { onOpenChange(false); setTimeout(() => onRequestDemo && onRequestDemo('tour-s6'), 120); }}
                        data-testid="roi-tour-cta-request-demo"
                      >
                        {tr('Descubre el ROI de tu propiedad — Solicita una demo personal',
                            `Discover Your Property's ROI — Request a Personal Demo`)}
                      </Button>
                      <Button
                        variant="outline"
                        className="border-2 border-navy-deep text-navy-deep hover:bg-navy-deep hover:text-white rounded-sm px-6 py-5 font-semibold"
                        onClick={() => { onOpenChange(false); setTimeout(() => onGetStarted && onGetStarted(), 120); }}
                        data-testid="roi-tour-cta-start-trial"
                      >
                        {tr('Comenzar prueba gratuita', 'Start Free Trial')}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {tr('Prueba 14 días · Cancela cuando quieras · Pago seguro con Stripe',
                          '14-day trial · Cancel anytime · Secure checkout with Stripe')}
                    </p>
                  </div>
                </StepFrame>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between px-5 md:px-7 py-4 border-t border-border bg-white">
          <Button
            variant="ghost"
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={stepIdx === 0}
            data-testid="roi-tour-prev"
            className="gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> {tr('Anterior', 'Prev')}
          </Button>
          <div className="hidden md:flex items-center gap-1" data-testid="roi-tour-dots">
            {steps.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStepIdx(i)}
                aria-label={`${tr('Ir al paso', 'Go to step')} ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${i === stepIdx ? 'bg-gold-metallic' : 'bg-navy-deep/15 hover:bg-navy-deep/40'}`}
              />
            ))}
          </div>
          {stepIdx < TOTAL - 1 ? (
            <Button
              onClick={() => setStepIdx((i) => Math.min(TOTAL - 1, i + 1))}
              className="bg-navy-deep hover:bg-navy-deep/90 text-white rounded-sm gap-1"
              data-testid="roi-tour-next"
            >
              {tr('Siguiente', 'Next')} <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              data-testid="roi-tour-finish"
            >
              {tr('Terminar', 'Finish')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- ROI dial (radial) ----------
const RoiDial = ({ label, value, highlight = false }) => {
  const data = [{ name: label, value: Math.min(20, value), fill: highlight ? '#D4AF37' : '#1A2B4A' }];
  return (
    <div className={`bg-white border rounded-sm p-3 flex flex-col items-center ${highlight ? 'border-gold-metallic' : 'border-border'}`} data-testid={`roi-dial-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-full h-24">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={210} endAngle={-30}>
            <RadialBar dataKey="value" cornerRadius={4} background={{ fill: '#F0E6D2' }} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      <div className="font-playfair text-xl text-navy-deep -mt-5">{pct(value)}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1 text-center">{label}</div>
    </div>
  );
};

// ---------- S2 scripted AI CFO ----------
const AiCfoStep = ({ step, isEs, tr }) => {
  const [visible, setVisible] = useState(0);
  const lines = isEs
    ? [
        { who: 'user', text: '¿Dónde estoy gastando por encima de la mediana?' },
        { who: 'ai',   text: 'Revisé tus 3 villas y encontré 3 líneas por encima de la mediana del portafolio:' },
        { who: 'ai',   text: '• Casamar — mantenimiento de piscina +38% vs trimestre anterior.' },
        { who: 'ai',   text: '• Villa Claude — jardinería ≈ 2.1× la mediana del portafolio.' },
        { who: 'ai',   text: '• Palm Jumeirah — service charges al borde del rango habitual.' },
        { who: 'ai',   text: 'Son oportunidades a revisar con tus proveedores, no ahorros comprometidos.' },
      ]
    : [
        { who: 'user', text: 'Where am I spending above the median?' },
        { who: 'ai',   text: 'I reviewed your 3 villas and found 3 lines above the portfolio median:' },
        { who: 'ai',   text: '• Casamar — pool maintenance +38% vs previous quarter.' },
        { who: 'ai',   text: '• Villa Claude — landscaping ≈ 2.1× the portfolio median.' },
        { who: 'ai',   text: '• Palm Jumeirah — service charges at the top of the usual band.' },
        { who: 'ai',   text: 'These are opportunities to review with your vendors, not committed savings.' },
      ];
  const timers = useRef([]);
  useEffect(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
    setVisible(0);
    lines.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisible((v) => Math.max(v, i + 1)), 350 + i * 550));
    });
    return () => timers.current.forEach((t) => clearTimeout(t));
  }, [isEs]); // eslint-disable-line
  return (
    <StepFrame icon={step.icon} eyebrow={step.eyebrow} title={step.title} outcome={step.outcome} testid="roi-tour-step-2">
      <div className="bg-white border border-border rounded-sm p-4 space-y-3 min-h-[260px]" data-testid="ai-cfo-chat">
        {lines.slice(0, visible).map((l, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className={`flex ${l.who === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm ${
              l.who === 'user' ? 'bg-navy-deep text-white' : 'bg-sand-warm text-navy-deep border border-border'
            }`}>
              {l.text}
            </div>
          </motion.div>
        ))}
      </div>
      <Disclosure testid="roi-disclosure-s2">
        {tr(
          'El AI CFO identifica oportunidades a partir de tus datos. Los ahorros reales dependen de la negociación con proveedores y la aprobación del propietario.',
          'The AI CFO identifies opportunities from your data. Actual savings depend on vendor negotiation and owner approval.'
        )}
      </Disclosure>
    </StepFrame>
  );
};

// ---------- S4 data ----------
const EXPENSE_MIX = [
  { key: 'maintenance',  name: 'Maintenance',     es: 'Mantenimiento',   value: 28 },
  { key: 'housekeeping', name: 'Housekeeping',    es: 'Housekeeping',    value: 24 },
  { key: 'utilities',    name: 'Utilities',       es: 'Servicios',       value: 18 },
  { key: 'insurance',    name: 'Insurance',       es: 'Seguros',         value: 11 },
  { key: 'management',   name: 'Management',      es: 'Gestión',         value: 10 },
  { key: 'other',        name: 'Other',           es: 'Otros',           value: 9  },
];
const PIE_COLORS = ['#1A2B4A', '#D4AF37', '#B8860B', '#8B6914', '#6B8E23', '#A89A6D'];

export default RoiGuidedDemoModal;
